// https://github.com/Rowadz/redux-toolkit-adapters-and-thunks/blob/main/src/features/comments/commentsSlice.js
import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit'
import _ from 'lodash'
import { bshgenAPI } from '~/api'
import { getPersistedItem, setPersistedItem } from '~/hooks'
import { stripHtmlTags } from '~/utils'
import { buildExtraReducers } from './utils'

const name = 'BSH'

const treeAdapter = createEntityAdapter({
  selectId: (brand) => brand.url,
})
const categoriesAdapter = createEntityAdapter()
const paragraphsAdapter = createEntityAdapter()
const assignablePropertiesAdapter = createEntityAdapter()
const productsAdapter = createEntityAdapter()
const propertiesAdapter = createEntityAdapter()
const propertiesGroupsAdapter = createEntityAdapter()
const shopsAdapter = createEntityAdapter()
const categoryParagraphsAdapter = createEntityAdapter()
const productParagraphsAdapter = createEntityAdapter()
const exportsAdapter = createEntityAdapter()
const empCoAdapter = createEntityAdapter()

export const slice = createSlice({
  name,
  initialState: treeAdapter.getInitialState({
    pendingRequests: {},
    categories: categoriesAdapter.getInitialState(),
    paragraphs: paragraphsAdapter.getInitialState(),
    assignableProperties: assignablePropertiesAdapter.getInitialState(),
    products: productsAdapter.getInitialState(),
    properties: propertiesAdapter.getInitialState(),
    propertiesGroups: propertiesGroupsAdapter.getInitialState({
      other: undefined,
    }),
    shops: shopsAdapter.getInitialState(),
    categoryParagraphs: categoryParagraphsAdapter.getInitialState(),
    productParagraphs: productParagraphsAdapter.getInitialState(),
    exports: exportsAdapter.getInitialState(),
    empCo: empCoAdapter.getInitialState(),
    empCoSelected: null,
    interaction: getPersistedItem('interaction') ?? 'buttons',
  }),
  reducers: {
    treeReceived(state, { payload }) {
      if (Array.isArray(payload)) {
        const categories = []
        const products = []
        const tree = payload.map((brand) => {
          const addCategories = (items, other) => {
            if (!items) return
            return items.map((category) => {
              category = {
                ...category,
                ...other,
              }
              categories.push(category)
              const parents = [
                ...other.parents,
                { model: category.model, id: category.id },
              ]
              if (Array.isArray(category.categories)) {
                category.categories = addCategories(category.categories, {
                  category_id: category.id,
                  parents,
                })
              }
              if (Array.isArray(category.products)) {
                category.products = category.products.map((product) => {
                  product = {
                    ...product,
                    category_id: category.id,
                    parents,
                  }
                  products.push(product)
                  return product.id
                })
              }
              return category.id
            })
          }
          brand.categories = addCategories(brand.categories, {
            brand_id: brand.url,
            parents: [{ model: 'brand', id: brand.url }],
          })
          return brand
        })
        treeAdapter.setAll(state, tree)
        categoriesAdapter.setAll(state.categories, categories)
        productsAdapter.setAll(state.products, products)
      }
    },
    updateParagraph(state, { payload }) {
      paragraphsAdapter.upsertOne(state.paragraphs, payload)
    },
    updateParagraphs(state, { payload }) {
      const { category_paragraphs, paragraphs, properties, shops, values } =
        payload
      if (shops) {
        shopsAdapter.setAll(state.shops, shops)
      }
      if (category_paragraphs) {
        categoryParagraphsAdapter.setAll(
          state.categoryParagraphs,
          category_paragraphs
        )
      }
      paragraphsAdapter.setAll(state.paragraphs, paragraphs)
      if (assignableProperties) {
        assignablePropertiesAdapter.setAll(
          state.assignableProperties,
          properties?.map((p) => ({ ...p, values: values[p.id] ?? [] })) ?? []
        )
      }
    },
    setEmpCo(state, { payload }) {
      const rows = Array.isArray(payload) ? payload : []
      empCoAdapter.setAll(state.empCo, rows)
      state.empCoSelected =
        rows.find((row) => row.id === state.empCoSelected?.id) ?? null
    },
    clearParagraphs(state) {
      paragraphsAdapter.removeAll(state.paragraphs)
    },
    updateProduct(state, { payload }) {
      productsAdapter.upsertOne(state.products, payload)
    },
    updateProducts(state, { payload }) {
      productsAdapter.upsertMany(state.products, payload)
    },
    updateProductParagraphs(state, { payload }) {
      productParagraphsAdapter.setAll(state.productParagraphs, payload)
    },
    updateProperties(state, { payload }) {
      const properties = []
      const groups = payload?.groups?.map((group) => {
        group.properties = group.properties.map((property) => {
          properties.push(property)
          return property.id
        })
        return group
      })
      const other = payload?.other?.map((property) => {
        properties.push(property)
        return property.id
      })
      propertiesAdapter.setMany(state.properties, properties)
      if (payload?.allGroups === false) {
        propertiesGroupsAdapter.setMany(state.propertiesGroups, groups)
      } else {
        propertiesGroupsAdapter.setAll(state.propertiesGroups, groups)
      }
      if (other) {
        state.propertiesGroups.other = other
      }
      if (payload.products) {
        productsAdapter.upsertMany(state.products, payload.products)
      }
    },
    updatePropertiesGroupsOrder(state, { payload }) {
      state.propertiesGroups.ids = payload
    },
    updatePropertiesOrder(state, { payload }) {
      const { groups, other } = payload
      groups.forEach(({ id, properties }) => {
        state.propertiesGroups.entities[id].properties = properties
      })
      state.propertiesGroups.other = other
    },
    updateExports(state, { payload }) {
      exportsAdapter.setAll(state.exports, payload)
    },
    updateEmpCo(state, { payload }) {
      empCoAdapter.upsertOne(state.empCo, payload)
      if (state.empCoSelected?.id === payload?.id) {
        state.empCoSelected = payload
      }
    },
    removeEmpCo(state, { payload }) {
      empCoAdapter.removeOne(state.empCo, payload)
      if (state.empCoSelected?.id === payload) {
        state.empCoSelected = null
      }
    },
    selectEmpCo(state, { payload }) {
      state.empCoSelected = payload
    },
    setInteraction(state, { payload }) {
      state.interaction = payload
      setPersistedItem('interaction', payload)
    },
  },
  extraReducers: (builder) => {
    buildExtraReducers(builder, [
      [getTree],
      [createCategory],
      [removeCategory],
      [moveProduct],
      [getProperties],
      [resetProperties],
      [reorderPropertiesGroups],
      [reorderProperties],
      [savePropertiesGroup],
      [deletePropertiesGroup],
      [createCustomProperty],
      [storeProperty],
      [togglePropertySkip],
      [toggleArchive],
      [nextStatus],
      [loadMatrix],
      [saveMatrix],
      [getParagraphs],
      [getProductParagraphs],
      [generateParagraphs],
      [removeParagraphs],
      [copyParagraphsFrom],
      [layoutParagraphs],
      [reorderParagraphs],
      [createParagraph],
      [saveParagraph],
      [restoreParagraphToParent],
      [uploadParagraphImage],
      [deleteParagraphImage],
      [assignSideIconFromIconParagraph],
      [updatePropertyAssigns],
      [getExports],
      [updateExport],
      [createXML],
    ])
  },
})

/**
 * actions
 */

const {
  treeReceived,
  updateParagraph,
  updateParagraphs,
  clearParagraphs,
  updateProduct,
  updateProducts,
  updateProductParagraphs,
  updateProperties,
  updatePropertiesGroupsOrder,
  updatePropertiesOrder,
  updateExports,
  selectEmpCo,
  setInteraction,
} = slice.actions

export { clearParagraphs, selectEmpCo, setInteraction }

export const getTree = createAsyncThunk(
  `${name}/getTree`,
  async (settings, thunkAPI) => {
    const result = await bshgenAPI('tree', settings)
    thunkAPI.dispatch(treeReceived(result))
  }
)

const updateItem = async (settings) => {
  const {
    data: { model, id, ...data },
    ...rest
  } = settings
  return await bshgenAPI(`${model}/${id}`, { method: 'PUT', data, ...rest })
}

export const deleteItem = async (settings) => {
  const {
    data: { model, id },
    ...rest
  } = settings
  return await bshgenAPI(`${model}/${id}`, { method: 'DELETE', ...rest })
}

export const createCategory = createAsyncThunk(
  `${name}/createCategory`,
  async (settings, thunkAPI) => {
    const {
      data: { brand, ...data },
      ...rest
    } = settings
    const result = await bshgenAPI(`${brand.url}/category`, {
      method: 'POST',
      data,
      ...rest,
    })
    thunkAPI.dispatch(treeReceived(result))
    const state = thunkAPI.getState()
    if (!data.parent_id) {
      const bc = brandCategories(state, brand.url)
      return categories(state, bc).find((c) => c.name === data.name)
    } else {
      return categories(
        state,
        categoryById(state, data.parent_id).categories
      ).find((c) => c.name === data.name)
    }
  }
)

export const removeCategory = createAsyncThunk(
  `${name}/removeCategory`,
  async (settings, thunkAPI) => {
    const result = await deleteItem(settings)
    await thunkAPI.dispatch(getTree())
    return result
  }
)

export const moveProduct = createAsyncThunk(
  `${name}/moveProduct`,
  async (settings, thunkAPI) => {
    const {
      data: { product, category },
      ...rest
    } = settings
    const result = await bshgenAPI(`move-product/${product.id}`, {
      method: 'PUT',
      data: {
        category_id: category.id,
      },
      ...rest,
    })
    thunkAPI.dispatch(treeReceived(result))
    return result
  }
)

export const getProperties = createAsyncThunk(
  `${name}/getProperties`,
  async (settings, thunkAPI) => {
    const {
      data: { owner, productId },
      ...rest
    } = settings
    const parts = [owner.model, owner.id]
    if (productId) {
      parts.push('product', productId)
    }
    parts.push('properties')
    const result = await bshgenAPI(parts.join('/'), rest)
    await thunkAPI.dispatch(updateProperties(result))
    return result
  }
)

export const resetProperties = createAsyncThunk(
  `${name}/resetProperties`,
  async (settings, thunkAPI) => {
    const {
      data: { model, id },
      ...rest
    } = settings
    const result = await bshgenAPI(`${model}/${id}/reset/properties`, rest)
    thunkAPI.dispatch(updateProperties(result))
  }
)

export const reorderPropertiesGroups = createAsyncThunk(
  `${name}/reorderPropertiesGroups`,
  async (settings, thunkAPI) => {
    const {
      data: { owner, ids },
      ...rest
    } = settings
    thunkAPI.dispatch(updatePropertiesGroupsOrder(ids))
    const result = await bshgenAPI(
      `${owner.model}/${owner.id}/reorder/groups`,
      { method: 'POST', data: { items: ids }, ...rest }
    )
    thunkAPI.dispatch(updatePropertiesGroupsOrder(result))
  }
)

export const reorderProperties = createAsyncThunk(
  `${name}/reorderProperties`,
  async (settings, thunkAPI) => {
    const {
      data: { owner, data },
      ...rest
    } = settings
    thunkAPI.dispatch(updatePropertiesOrder(data))
    const result = await bshgenAPI(
      `${owner.model}/${owner.id}/reorder/properties`,
      { method: 'POST', data, ...rest }
    )
    thunkAPI.dispatch(updateProperties(result))
  }
)

export const savePropertiesGroup = createAsyncThunk(
  `${name}/savePropertiesGroup`,
  async (settings, thunkAPI) => {
    const {
      data: { owner, name },
      ...rest
    } = settings
    const result = await bshgenAPI(`${owner.model}/${owner.id}/groups`, {
      method: 'POST',
      data: { name },
      ...rest,
    })
    await thunkAPI.dispatch(updateProperties(result))
    return result
  }
)

export const deletePropertiesGroup = createAsyncThunk(
  `${name}/deletePropertiesGroup`,
  async (settings, thunkAPI) => {
    const {
      data: { owner },
      ...rest
    } = settings
    const result = await bshgenAPI(`${owner.model}/${owner.id}`, {
      method: 'DELETE',
      ...rest,
    })
    await thunkAPI.dispatch(updateProperties(result))
    return result
  }
)

export const createCustomProperty = createAsyncThunk(
  `${name}/createCustomProperty`,
  async (settings, thunkAPI) => {
    const {
      data: { owner, ...data },
      ...rest
    } = settings
    const result = await bshgenAPI(`${owner.model}/${owner.id}/properties`, {
      method: 'POST',
      data,
      ...rest,
    })
    await thunkAPI.dispatch(updateProperties({ ...result, allGroups: false }))
    return result
  }
)

export const storeProperty = createAsyncThunk(
  `${name}/storeProperty`,
  async (settings, thunkAPI) => {
    const {
      data: { owner, property },
      ...rest
    } = settings
    const data = {
      description: property.description,
    }
    if (owner.model === 'product') {
      data.value = property.value
    }
    const result = await bshgenAPI(
      `${property.model}/${property.id}/${owner.model}/${owner.id}`,
      {
        method: 'POST',
        data,
        ...rest,
      }
    )
    await thunkAPI.dispatch(updateProperties({ ...result, allGroups: false }))
    return result
  }
)

export const togglePropertySkip = createAsyncThunk(
  `${name}/togglePropertySkip`,
  async (settings) => {
    const {
      data: { property, group, ...data },
      ...rest
    } = settings
    const result = await bshgenAPI(
      `${property.model}/${property.id}/${group.model}/${group.id}/toggle-skip`,
      {
        method: 'POST',
        data,
        ...rest,
      }
    )
    return result
  }
)

export const toggleArchive = createAsyncThunk(
  `${name}/toggleArchive`,
  async (settings, thunkAPI) => {
    const {
      data: { model, id },
      ...rest
    } = settings
    const result = await bshgenAPI(`${model}/${id}/toggle-archive`, rest)
    thunkAPI.dispatch(updateProduct(result))
  }
)

export const nextStatus = createAsyncThunk(
  `${name}/nextStatus`,
  async (settings, thunkAPI) => {
    const {
      data: { model, id },
      ...rest
    } = settings
    const result = await bshgenAPI(`${model}/${id}/next-status`, rest)
    thunkAPI.dispatch(updateProduct(result))
  }
)

export const loadMatrix = createAsyncThunk(
  `${name}/loadMatrix`,
  async (settings) => {
    const {
      data: {
        property_id,
        category: { model, id },
      },
      ...rest
    } = settings
    return await bshgenAPI(`${model}/${id}/matrix/${property_id}`, rest)
  }
)

export const saveMatrix = createAsyncThunk(
  `${name}/saveMatrix`,
  async (settings, thunkAPI) => {
    const {
      data: {
        category: { model, id },
        property_id,
        products,
      },
      ...rest
    } = settings
    const result = await bshgenAPI(`${model}/${id}/matrix/${property_id}`, {
      method: 'POST',
      data: { products },
      ...rest,
    })
    await thunkAPI.dispatch(updateProducts(result.products))
    return result
  }
)

export const getParagraphs = createAsyncThunk(
  `${name}/getParagraphs`,
  async (settings, thunkAPI) => {
    const {
      data: { owner },
      ...rest
    } = settings
    const result = await bshgenAPI(
      `${owner.model}/${owner.id}/paragraphs`,
      rest
    )
    thunkAPI.dispatch(updateParagraphs(result))
    return result
  }
)

export const getProductParagraphs = createAsyncThunk(
  `${name}/getProductParagraphs`,
  async (settings, thunkAPI) => {
    const {
      data: { product },
      ...rest
    } = settings
    const result = await bshgenAPI(
      `${product.model}/${product.id}/paragraphs`,
      rest
    )
    thunkAPI.dispatch(updateProductParagraphs(result.paragraphs))
    return result
  }
)

export const generateParagraphs = createAsyncThunk(
  `${name}/generateParagraphs`,
  async (settings, thunkAPI) => {
    const {
      data: { product },
      ...rest
    } = settings
    const result = await bshgenAPI(
      `${product.model}/${product.id}/auto-generate`,
      rest
    )
    if (result) {
      thunkAPI.dispatch(updateParagraphs(result))
    }
    return result
  }
)

export const removeParagraphs = createAsyncThunk(
  `${name}/removeParagraphs`,
  async (settings, thunkAPI) => {
    const {
      data: { product },
      ...rest
    } = settings
    const result = await bshgenAPI(
      `${product.model}/${product.id}/paragraphs`,
      {
        method: 'DELETE',
        ...rest,
      }
    )
    if (result) {
      thunkAPI.dispatch(updateParagraphs(result))
    }
    return result
  }
)

export const copyParagraphsFrom = createAsyncThunk(
  `${name}/copyParagraphsFrom`,
  async (settings, thunkAPI) => {
    const {
      data: { product, source },
      ...rest
    } = settings
    const result = await bshgenAPI(
      `${product.model}/${product.id}/copy-paragraphs`,
      {
        method: 'POST',
        data: { id: source.id, model: source.model },
        ...rest,
      }
    )
    if (result) {
      thunkAPI.dispatch(updateParagraphs(result))
    }
    return result
  }
)

export const layoutParagraphs = createAsyncThunk(
  `${name}/layoutParagraphs`,
  async (settings, thunkAPI) => {
    const {
      data: { product },
      ...rest
    } = settings
    const result = await bshgenAPI(
      `${product.model}/${product.id}/layout-paragraphs`,
      rest
    )
    if (result) {
      thunkAPI.dispatch(updateParagraphs(result))
    }
    return result
  }
)

export const reorderParagraphs = createAsyncThunk(
  `${name}/reorderParagraphs`,
  async (settings, thunkAPI) => {
    const {
      data: { owner, ids },
      ...rest
    } = settings
    await bshgenAPI(`${owner.model}/${owner.id}/paragraphs/reorder`, {
      method: 'POST',
      data: { items: ids },
      ...rest,
    })
    await thunkAPI.dispatch(getParagraphs({ data: { owner } }))
  }
)

export const createParagraph = createAsyncThunk(
  `${name}/createParagraph`,
  async (settings, thunkAPI) => {
    const {
      data: { owner, ...data },
      ...rest
    } = settings
    const result = await bshgenAPI(`${owner.model}/${owner.id}/paragraphs`, {
      method: 'POST',
      data,
      ...rest,
    })
    if (result) {
      thunkAPI.dispatch(updateParagraphs(result))
    }
    return result
  }
)

export const removeParagraph = createAsyncThunk(
  `${name}/removeParagraph`,
  async (settings) => {
    return await deleteItem(settings)
  }
)

export const saveParagraph = createAsyncThunk(
  `${name}/saveParagraph`,
  async (settings, thunkAPI) => {
    const {
      data: { model, id, overwrite, product, ...data },
      ...rest
    } = settings
    const result = await bshgenAPI(
      `${model}/${id}${product ? '' : overwrite ? '/overwrite' : '/vib'}`,
      {
        method: 'PUT',
        data,
        ...rest,
      }
    )
    if (result) {
      thunkAPI.dispatch(updateParagraph(result))
    }
    return result
  }
)

export const saveParagraphAlt = createAsyncThunk(
  `${name}/saveParagraphAlt`,
  async (settings, thunkAPI) => {
    const {
      data: {
        paragraph: { model, id, alt },
      },
      ...rest
    } = settings
    const result = await bshgenAPI(`${model}/${id}/alt`, {
      method: 'PUT',
      data: {
        alt,
      },
      ...rest,
    })
    thunkAPI.dispatch(updateParagraph(result))
    return result
  }
)

export const saveParagraphCaption = createAsyncThunk(
  `${name}/saveParagraphCaption`,
  async (settings, thunkAPI) => {
    const {
      data: {
        paragraph: { model, id, caption },
      },
      ...rest
    } = settings
    const result = await bshgenAPI(`${model}/${id}/caption`, {
      method: 'PUT',
      data: {
        caption,
      },
      ...rest,
    })
    thunkAPI.dispatch(updateParagraph(result))
    return result
  }
)

export const diffParagraphFromParent = createAsyncThunk(
  `${name}/diffParagraphFromParent`,
  async (settings) => {
    const {
      data: {
        paragraph: { model, id },
        fields,
      },
      ...rest
    } = settings
    const result = await bshgenAPI(`${model}/${id}/diff-from-parent`, {
      method: 'POST',
      data: { fields },
      ...rest,
    })
    return result
  }
)

export const restoreParagraphToParent = createAsyncThunk(
  `${name}/restoreParagraphToParent`,
  async (settings, thunkAPI) => {
    const {
      data: {
        paragraph: { model, id },
        fields,
      },
      ...rest
    } = settings
    const result = await bshgenAPI(`${model}/${id}/restore-to-parent`, {
      method: 'PUT',
      data: { fields },
      ...rest,
    })
    thunkAPI.dispatch(updateParagraph(result))
    return result
  }
)

export const restoreParagraphShopToParent = createAsyncThunk(
  `${name}/restoreParagraphShopToParent`,
  async (settings, thunkAPI) => {
    const {
      data: {
        paragraph: { model, id },
        shop,
      },
      ...rest
    } = settings
    const result = await bshgenAPI(
      `${model}/${id}/restore-to-parent/shop/${shop}`,
      rest
    )
    thunkAPI.dispatch(updateParagraph(result))
    return result
  }
)

export const uploadParagraphImage = createAsyncThunk(
  `${name}/uploadParagraphImage`,
  async (settings, thunkAPI) => {
    const {
      data: { paragraph, file, type },
      ...rest
    } = settings
    const data = type ? { type } : undefined
    const result = await bshgenAPI(
      `${paragraph.model}/${paragraph.id}/upload`,
      {
        method: 'POST',
        files: [file],
        ...(data ? { data } : {}),
        ...rest,
      }
    )
    thunkAPI.dispatch(updateParagraph(result))
    return result
  }
)

export const deleteParagraphImage = createAsyncThunk(
  `${name}/deleteParagraphImage`,
  async (settings, thunkAPI) => {
    const {
      data: { paragraph, type },
      ...rest
    } = settings
    const data = type ? { type } : undefined
    const result = await bshgenAPI(
      `${paragraph.model}/${paragraph.id}/picture`,
      {
        method: 'DELETE',
        ...(data ? { data } : {}),
        ...rest,
      }
    )
    thunkAPI.dispatch(updateParagraph(result))
    return result
  }
)

export const assignSideIconFromIconParagraph = createAsyncThunk(
  `${name}/assignSideIconFromIconParagraph`,
  async (settings, thunkAPI) => {
    const {
      data: { paragraph, sourceParagraphId },
      ...rest
    } = settings
    const result = await bshgenAPI(
      `${paragraph.model}/${paragraph.id}/side-icon-from-icon-paragraph`,
      {
        method: 'POST',
        data: { source_paragraph_id: sourceParagraphId },
        ...rest,
      }
    )
    thunkAPI.dispatch(updateParagraph(result))
    return result
  }
)

export const updatePropertyAssigns = createAsyncThunk(
  `${name}/updatePropertyAssigns`,
  async (settings, thunkAPI) => {
    const {
      data: { paragraph, assigns },
      ...rest
    } = settings
    const result = await bshgenAPI(
      `${paragraph.model}/${paragraph.id}/assigns`,
      {
        method: 'PUT',
        data: assigns,
        ...rest,
      }
    )
    thunkAPI.dispatch(updateParagraph(result))
    return result
  }
)

export const saveComparsionProducts = createAsyncThunk(
  `${name}/saveComparsionProducts`,
  async (settings, thunkAPI) => {
    const {
      data: { paragraph, products },
      ...rest
    } = settings
    const result = await bshgenAPI(
      `${paragraph.model}/${paragraph.id}/comparsion/products`,
      {
        method: 'PUT',
        data: products,
        ...rest,
      }
    )
    thunkAPI.dispatch(updateParagraph(result))
    return result
  }
)

export const getExports = createAsyncThunk(
  `${name}/getExports`,
  async (settings, thunkAPI) => {
    const {
      data: { owner },
      ...rest
    } = settings
    const result = await bshgenAPI(`exports/${owner.model}/${owner.id}`, rest)
    thunkAPI.dispatch(updateExports(result))
    return result
  }
)

export const updateExport = createAsyncThunk(
  `${name}/updateExport`,
  async (settings) => await updateItem(settings)
)

export const createXML = createAsyncThunk(
  `${name}/createXML`,
  async (settings, thunkAPI) => {
    const {
      data: { url },
      ...rest
    } = settings
    const result = await bshgenAPI(url.replace(/^\/api\//, ''), rest)
    thunkAPI.dispatch(updateExports(result))
    return result
  }
)

export const getEmpCo = createAsyncThunk(
  `${name}/getEmpCo`,
  async (brandUrl, thunkAPI) => {
    thunkAPI.dispatch(slice.actions.setEmpCo([]))
    const rows = await bshgenAPI(`${brandUrl}/emp-co`)
    thunkAPI.dispatch(slice.actions.setEmpCo(rows))
    return rows
  }
)

export const createEmpCo = createAsyncThunk(
  `${name}/createEmpCo`,
  async (settings, thunkAPI) => {
    const {
      data: { brandUrl, ...data },
      ...rest
    } = settings
    const created = await bshgenAPI(`${brandUrl}/emp-co`, {
      method: 'POST',
      data,
      ...rest,
    })
    thunkAPI.dispatch(slice.actions.updateEmpCo(created))
    return created
  }
)

export const saveEmpCo = createAsyncThunk(
  `${name}/saveEmpCo`,
  async (settings, thunkAPI) => {
    const {
      data: { brandUrl, id, ...data },
      ...rest
    } = settings
    const updated = await bshgenAPI(`${brandUrl}/emp-co/${id}`, {
      method: 'PUT',
      data,
      ...rest,
    })
    if (updated) {
      thunkAPI.dispatch(slice.actions.updateEmpCo(updated))
    }
    return updated
  }
)

export const removeEmpCo = createAsyncThunk(
  `${name}/removeEmpCo`,
  async (settings, thunkAPI) => {
    const {
      data: { brandUrl, id },
      ...rest
    } = settings
    const result = await bshgenAPI(`${brandUrl}/emp-co/${id}`, {
      method: 'DELETE',
      ...rest,
    })
    thunkAPI.dispatch(slice.actions.removeEmpCo(id))
    return result
  }
)
/**
 * selectors
 */

const treeSelectors = treeAdapter.getSelectors((state) => state[name])
const categoriesSelectors = categoriesAdapter.getSelectors(
  (state) => state[name].categories
)
const paragraphsSelectors = paragraphsAdapter.getSelectors(
  (state) => state[name].paragraphs
)
const assignablePropertiesSelectors = assignablePropertiesAdapter.getSelectors(
  (state) => state[name].assignableProperties
)
const productsSelectors = treeAdapter.getSelectors(
  (state) => state[name].products
)
const propertiesSelectors = treeAdapter.getSelectors(
  (state) => state[name].properties
)
const propertiesGroupsSelectors = treeAdapter.getSelectors(
  (state) => state[name].propertiesGroups
)
const shopsSelectors = shopsAdapter.getSelectors((state) => state[name].shops)
const categoryParagraphsSelectors = categoryParagraphsAdapter.getSelectors(
  (state) => state[name].categoryParagraphs
)
const productParagraphsSelectors = productParagraphsAdapter.getSelectors(
  (state) => state[name].productParagraphs
)
const exportsSelectors = exportsAdapter.getSelectors(
  (state) => state[name].exports
)
const empCoSelectors = empCoAdapter.getSelectors((state) => state[name].empCo)
export const isPendingRequest = (state, actions) => {
  return createSelector(
    [(state) => state[name].pendingRequests, (state, actions) => actions],
    (pendingRequests, actions) => {
      if (!actions) {
        return Object.keys(pendingRequests).length > 0
      }
      const result = Object.keys(pendingRequests).filter((action) =>
        actions.includes(action)
      )
      return result.length > 0
    }
  )(state, actions)
}

// export const ready = (state) => state[name].ready
export const ready = createSelector(
  (state) => state,
  (state) => treeSelectors.selectTotal(state) > 0
)

export const brands = createSelector(
  (state) => state,
  (state) => treeSelectors.selectAll(state)
)

export const brandCategories = createSelector(
  [(state) => state, (state, brand_url) => brand_url],
  (state, brand_url) => treeSelectors.selectById(state, brand_url)?.categories
)

export const treeByBrand = createSelector(
  [
    (state) => state,
    (state, brand_url) => brand_url,
    (state, brand_url, archive) => archive,
    (state, brand_url, archive, search) => search,
  ],
  (state, brand_url, archive, search) => {
    const tree = treeSelectors.selectById(state, brand_url)
    if (search) {
      search = search.toLowerCase()
      const productsIds = []
      let mainCategories = []
      let subCategories = []
      products(state)
        .filter(({ name, parents }) => {
          return (
            name.toLowerCase().includes(search) &&
            parents?.[0]?.id === brand_url
          )
        })
        .forEach(({ id, parents }) => {
          productsIds.push(id)
          mainCategories.push(parents?.[1]?.id)
          subCategories.push(parents?.[2]?.id)
        })
      mainCategories = _.uniq(mainCategories)
      subCategories = _.uniq(subCategories)
      return categories(
        state,
        tree.categories.filter((id) => mainCategories.includes(id))
      ).map(({ id, name, categories: children }) => ({
        id: `category/${id}`,
        name,
        children: categories(
          state,
          children.filter((id) => subCategories.includes(id))
        ).map(({ id, name, products: childrenSource }) => {
          let children = products(
            state,
            childrenSource.filter((id) => productsIds.includes(id))
          )
          if (!archive) {
            children = children.filter(({ archived_at }) => !archived_at)
          }
          return {
            id: `category/${id}`,
            name,
            children: children.map(({ id, name }) => ({
              id: `product/${id}`,
              name,
            })),
          }
        }),
      }))
    }
    return categories(state, tree.categories).map(
      ({ id, name, categories: children }) => ({
        id: `category/${id}`,
        name,
        children: categories(state, children).map(
          ({ id, name, products: childrenSource }) => {
            let children = products(state, childrenSource)
            if (!archive) {
              children = children.filter(({ archived_at }) => !archived_at)
            }
            return {
              id: `category/${id}`,
              name,
              children: children.map(({ id, name }) => ({
                id: `product/${id}`,
                name,
              })),
            }
          }
        ),
      })
    )
  }
)

export const treePath = createSelector(
  (state) => state,
  (state, obj) => obj,
  (state, { model, id }) => {
    let result
    switch (model) {
      case 'category':
        result = categoryById(state, id)
        break
      case 'product':
        result = productById(state, id)
        break
    }
    return result?.parents
  },
  (state, obj, parents) => {
    if (!Array.isArray(parents)) return
    // obj = cloneDeep(obj)
    delete obj.brand_url
    // return [...cloneDeep(parents), obj]
    return [...parents, obj]
  }
)

export const categories = createSelector(
  [(state) => state, (state, ids) => ids],
  (state, ids) => {
    if (Array.isArray(ids)) {
      const entities = categoriesSelectors.selectEntities(state)
      return ids.map((id) => entities[id]).filter(Boolean)
    } else {
      return categoriesSelectors.selectAll(state)
    }
  }
)

export const categoryById = createSelector(
  [(state) => state, (state, id) => id],
  (state, id) => categoriesSelectors.selectById(state, id)
)

export const paragraphs = createSelector(
  (state) => state,
  (state) => paragraphsSelectors.selectAll(state)
)

export const assignableProperties = createSelector(
  (state) => state,
  (state) => assignablePropertiesSelectors.selectAll(state)
)

export const products = createSelector(
  [(state) => state, (state, ids) => ids],
  (state, ids) => {
    if (Array.isArray(ids)) {
      const entities = productsSelectors.selectEntities(state)
      return ids.map((id) => entities[id]).filter(Boolean)
    } else {
      return productsSelectors.selectAll(state)
    }
  }
)

export const productById = createSelector(
  [(state) => state, (state, id) => id],
  (state, id) => productsSelectors.selectById(state, id)
)

export const properties = createSelector(
  [(state) => state, (state, ids) => ids],
  (state, ids) => {
    if (Array.isArray(ids)) {
      const entities = propertiesSelectors.selectEntities(state)
      return ids.map((id) => entities[id]).filter(Boolean)
    } else {
      return propertiesSelectors.selectAll(state)
    }
  }
)

export const propertiesGroups = createSelector(
  (state) => state,
  (state) => propertiesGroupsSelectors.selectAll(state)
)

export const propertiesGroupsOther = createSelector(
  (state) => state,
  (state) => state[name].propertiesGroups.other
)

export const shops = createSelector(
  (state) => state,
  (state) => shopsSelectors.selectAll(state)
)

export const categoryParagraphs = createSelector(
  (state) => state,
  (state) => categoryParagraphsSelectors.selectAll(state)
)

export const productParagraphs = createSelector(
  (state) => state,
  (state) => productParagraphsSelectors.selectAll(state)
)

export const exports = createSelector(
  (state) => state,
  (state) => exportsSelectors.selectAll(state)
)

export const empCoSelected = (state) => state[name].empCoSelected

const includesEmpCo = (needle, haystack) => {
  if (typeof haystack !== 'string') {
    return false
  }
  const cleanText = (text) => {
    return stripHtmlTags(text).toLowerCase().replace(/\s+/g, ' ').trim()
  }
  haystack = cleanText(haystack)
  needle = cleanText(needle)
  return haystack.includes(needle)
}

export const containsEmpCo = createSelector(
  [empCoSelected, (state, text) => text],
  (selectedEmpCo, text) => {
    return selectedEmpCo && includesEmpCo(selectedEmpCo.target, text)
  }
)

export function paragraphContains(paragraph, needle, variant) {
  if (variant) {
    if (paragraph?.shops && typeof paragraph.shops === 'object') {
      const shop = paragraph.shops[variant]
      return (
        includesEmpCo(needle, shop?.title || paragraph.title) ||
        includesEmpCo(needle, shop?.text || paragraph.text)
      )
    }
  } else {
    const common = [paragraph?.title, paragraph?.text, paragraph?.alt].some(
      (item) => includesEmpCo(needle, item)
    )
    if (common) {
      return true
    }
    if (paragraph?.type === 'icon') {
      return includesEmpCo(needle, paragraph?.media)
    }
    if (paragraph?.type?.includes('anim')) {
      return includesEmpCo(needle, paragraph?.caption)
    }
    return includesEmpCo(needle, paragraph?.icon_caption)
  }
  return false
}

export const empCo = createSelector(
  [
    (state) => state,
    (_state, params) => params?.shopId,
    (_state, params) => params?.skipCount,
  ],
  (state, shopId, skipCount) => {
    const rows = empCoSelectors.selectAll(state)
    const allParagraphs = paragraphsSelectors.selectAll(state)
    const shop = shopsSelectors.selectById(state, shopId)

    const withCounts = skipCount
      ? rows
      : rows.map((r) => {
          const target = r.target
          const count =
            target === null ||
            target === undefined ||
            target === '' ||
            String(target).trim() === ''
              ? 0
              : allParagraphs.filter((p) =>
                  paragraphContains(p, target, shop?.slug)
                ).length
          return { ...r, count }
        })

    const targetSortKey = (target) =>
      String(target ?? '')
        .toLocaleLowerCase('pl')
        .normalize('NFD')
        .replace(/\p{M}/gu, '')

    const isEmptyTarget = (target) => String(target ?? '').trim() === ''

    return _.orderBy(
      withCounts,
      [
        (r) => (isEmptyTarget(r.target) ? 1 : 0),
        (r) => r.count,
        (r) => targetSortKey(r.target),
      ],
      ['asc', 'desc', 'asc']
    )
  }
)

export const interaction = (state) => state[name].interaction
